/**
 * PageModal — right-side slide-over panel used for Dashboard, Watchlist,
 * and Change-Password sub-pages.
 *
 * Rendered via `ReactDOM.createPortal` into `#marketview`.
 * Features:
 * - Closes on Escape key or backdrop click
 * - Becomes pointer-events-transparent during chart-symbol drag-and-drop
 *   (so the user can still drop a symbol onto a chart pane while the panel
 *   is open)
 */
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./PageModal.scss";

interface PageModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  theme: string;
  children: React.ReactNode;
}

const PageModal: React.FC<PageModalProps> = ({
  isOpen,
  onClose,
  title,
  theme,
  children,
}) => {
  const [isDragThrough, setIsDragThrough] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Make overlay transparent to pointer events during chart-symbol drag
  useEffect(() => {
    if (!isOpen) return;
    const handleDragStart = () => setIsDragThrough(true);
    const handleDragEnd = () => setIsDragThrough(false);
    window.addEventListener("chart-symbol-drag-start", handleDragStart);
    window.addEventListener("chart-symbol-drag-end", handleDragEnd);
    return () => {
      window.removeEventListener("chart-symbol-drag-start", handleDragStart);
      window.removeEventListener("chart-symbol-drag-end", handleDragEnd);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const portalTarget = document.getElementById("marketview") || document.body;

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className={`page-modal-overlay ${theme}${isDragThrough ? " drag-through" : ""}`}
      onClick={handleBackdropClick}
      style={{ background: "none", backdropFilter: "none" }}
    >
      <div className="page-modal-container">
        <button
          className="page-modal-close"
          onClick={onClose}
          title="Close"
        >
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="4" y1="4" x2="16" y2="16" />
            <line x1="16" y1="4" x2="4" y2="16" />
          </svg>
        </button>
        <div className="page-modal-body">{children}</div>
      </div>
    </div>,
    portalTarget,
  );
};

export default PageModal;
